import { useEffect } from "react";
import { useBibleVersion } from "../context/bibleVersionContext";

const CATHOLIC_BIBLE_VERSIONS = ["NABRE", "DRA", "RSVCE", "NRSVCE"];

const DEUTEROCANONICAL_BOOKS = [
  "Tobit",
  "Judith",
  "Wisdom",
  "Wisdom of Solomon",
  "Sirach",
  "Ecclesiasticus",
  "Baruch",
  "Letter of Jeremiah",
  "Epistle of Jeremiah",
  "1 Maccabees",
  "2 Maccabees",
  "First Maccabees",
  "Second Maccabees",
  "Additions to Esther",
  "Prayer of Azariah",
  "Song of the Three Young Men",
  "Susanna",
  "Bel and the Dragon",
];

/* Exported so a caller that opens the reference itself -- rather than
   rendering an anchor for the global listener below to intercept -- can tell a
   real passage URL from this sentinel. */
export const BLOCKED_VERSE_URL = "#deuterocanonical-reference-blocked";

const BLOCKED_TOOLTIP =
  "Select a Catholic Bible version to open Deuterocanonical references.";

function normalizeReference(reference = "") {
  return reference.trim().replace(/\s+/g, " ").toLowerCase();
}

function isCatholicBibleVersion(version = "") {
  return CATHOLIC_BIBLE_VERSIONS.includes(version);
}

function isDeuterocanonicalReference(reference = "") {
  const normalizedReference = normalizeReference(reference);

  return DEUTEROCANONICAL_BOOKS.some((book) => {
    const normalizedBook = normalizeReference(book);

    return (
      normalizedReference === normalizedBook ||
      normalizedReference.startsWith(`${normalizedBook} `) ||
      normalizedReference.startsWith(`${normalizedBook}:`)
    );
  });
}

export function shouldBlockVerseReference(reference = "", bibleVersion = "") {
  return (
    isDeuterocanonicalReference(reference) &&
    !isCatholicBibleVersion(bibleVersion)
  );
}

export default function useVerseUrl() {
  const { bibleVersion } = useBibleVersion();

  useEffect(() => {
    function getBlockedAnchor(event) {
      return event.target.closest(`a[href="${BLOCKED_VERSE_URL}"]`);
    }

    function handleBlockedClick(event) {
      const anchor = getBlockedAnchor(event);

      if (!anchor) return;

      event.preventDefault();
      event.stopPropagation();

      alert(BLOCKED_TOOLTIP);
    }

    function handleBlockedHover(event) {
      const anchor = getBlockedAnchor(event);

      if (!anchor) return;

      anchor.setAttribute("title", BLOCKED_TOOLTIP);
      anchor.setAttribute("aria-label", BLOCKED_TOOLTIP);
    }

    document.addEventListener("click", handleBlockedClick, true);
    document.addEventListener("mouseover", handleBlockedHover, true);
    document.addEventListener("focusin", handleBlockedHover, true);

    return () => {
      document.removeEventListener("click", handleBlockedClick, true);
      document.removeEventListener("mouseover", handleBlockedHover, true);
      document.removeEventListener("focusin", handleBlockedHover, true);
    };
  }, []);

  return (verse) => {
    if (!verse) return "#";

    if (shouldBlockVerseReference(verse, bibleVersion)) {
      return BLOCKED_VERSE_URL;
    }

    return `https://www.biblegateway.com/passage/?search=${encodeURIComponent(
      verse
    )}&version=${bibleVersion}`;
  };
}